import { lazy, Suspense } from "react";

import type { Backend } from "./backend";
import { AppGate } from "./auth/AppGate";
import { BackendProvider } from "./app/BackendProvider";
import { StreamProvider } from "./app/StreamProvider";
import { useHashRoute } from "./app/useHashRoute";
import { ConfigProvider } from "./config/ConfigProvider";
import { LinesProvider } from "./config/LinesProvider";
import { PostProvider } from "./posts/PostProvider";
import { Cockpit } from "./cockpit/Cockpit";
import { NetworkScreen } from "./network/NetworkScreen";
import { ServicesScreen } from "./services/ServicesScreen";
import { SettingsScreen } from "./settings/SettingsScreen";
import { StatsScreen } from "./stats/StatsScreen";
import { VehiclesScreen } from "./vehicles/VehiclesScreen";

// La carte pilote embarque MapLibre (~290 kB gzip) : chargée à la demande pour
// ne pas alourdir l'écran principal. Le rejeu (P2-33) EST la carte pilote
// (`mode="replay"`, demande utilisateur 2026-09-24 — plus de carte séparée) :
// même chunk, pas un second import.
const PilotMap = lazy(() => import("./map/PilotMap").then((m) => ({ default: m.PilotMap })));
// L'écran d'administration est un outil séparé, rarement ouvert : idem.
const AdminScreen = lazy(() => import("./admin/AdminScreen").then((m) => ({ default: m.AdminScreen })));

/**
 * Point d'entrée.
 *
 * L'**administration** (`#/admin`) est un outil de plateforme : elle vit hors de
 * la pile de providers de la console (config réseau, poste, flux) — elle gère
 * elle-même sa session (jeton admin) et son choix de réseau, et reste joignable
 * même si la config d'un réseau ne charge pas.
 *
 * Les trois écrans d'exploitation partagent la pile :
 *   backend (REST + flux) → config pilotée serveur → référentiel des lignes →
 *   poste de régulation (résout son isolement contre les lignes) → flux
 *   temps réel partagé.
 *
 * `backend` n'est passé qu'en test / Storybook ; en production le provider le
 * crée depuis l'environnement.
 *
 * `AppGate` (porte d'accès applicative, contrat v1.38 §8bis) enveloppe TOUT :
 * ni la console, ni la carte, ni l'administration ne se rendent sans passer la
 * porte.
 */
export function App({ backend }: { backend?: Backend }) {
  return (
    <BackendProvider {...(backend ? { backend } : {})}>
      <AppGate>
        <RootRouter />
      </AppGate>
    </BackendProvider>
  );
}

function RootRouter() {
  const screen = useHashRoute();

  if (screen === "admin") {
    return (
      <Suspense fallback={<p className="p-4 has-text-grey">Chargement de l'administration…</p>}>
        <AdminScreen />
      </Suspense>
    );
  }

  return (
    <ConfigProvider>
      <LinesProvider>
        <PostProvider>
          <StreamProvider>
            <OperationScreens screen={screen} />
          </StreamProvider>
        </PostProvider>
      </LinesProvider>
    </ConfigProvider>
  );
}

function OperationScreens({
  screen,
}: {
  screen: "cockpit" | "map" | "network" | "vehicles" | "services" | "stats" | "replay" | "settings";
}) {
  if (screen === "map") {
    return (
      <Suspense fallback={<p className="p-4 has-text-grey">Chargement de la carte…</p>}>
        <PilotMap />
      </Suspense>
    );
  }
  if (screen === "replay") {
    return (
      <Suspense fallback={<p className="p-4 has-text-grey">Chargement du rejeu…</p>}>
        <PilotMap mode="replay" />
      </Suspense>
    );
  }
  if (screen === "network") return <NetworkScreen />;
  if (screen === "vehicles") return <VehiclesScreen />;
  if (screen === "services") return <ServicesScreen />;
  if (screen === "stats") return <StatsScreen />;
  if (screen === "settings") return <SettingsScreen />;
  return <Cockpit />;
}
